<template>
  <div
    ref="el"
    :class="{ 'mt-3': !noText }"
    class="cta pointer-events-all d-inline-flex align-center"
    :loading="!isInitialized"
    data-elbcontext="shopping:intent"
    id="call_to_action"
  >
    <VSelect
      v-if="isEnabled && options.length"
      aria-label="call_to_action"
      aria-describedby="call_to_action"
      :items="options"
      v-model="selectedQty"
      min-width="75"
      hide-details
      outlined
    />
    <VBtn
      aria-label="call_to_action"
      aria-describedby="call_to_action"
      :disabled="!isEnabled"
      data-elbaction="click:add_to_cart(product)"
      :loading="isPendingState"
      :data-elb-product="`qty:${selectedQty}`"
      min-width="75"
      @click="onAddToCart"
    >
      <SvgWrapper
        v-if="isEnabled"
        v-show="!isSuccessState"
        class="f-white"
        use="icon_svg_cart"
      />
      <SvgWrapper
        v-if="isEnabled"
        v-show="isSuccessState"
        class="f-white"
        use="icon_svg_circle_checkmark"
      />
      <span v-if="isEnabled && !noText" class="ml-3">{{
        translate('pdp_button_add_to_cart')
      }}</span>
      <span v-if="!isEnabled">{{ translate('pdp_button_sold_out') }}</span>
    </VBtn>
  </div>
</template>

<script>
import { useListId } from '@/composables/useListId.js'
import { defineAsyncComponent, ref } from 'vue'

const SvgWrapper = defineAsyncComponent(
  () => import('@/components/basic/atoms/SvgWrapper.vue')
)

const REQUEST_STATES = {
  NONE: 'none',
  PENDING: 'pending',
  FINISHED: 'finished'
}

const SUCCESS_STATE_DURATION = 1500

const _getModules = (() => {
  let cache

  return async () => {
    if (cache) return cache
    const [
      { getCartPayload, isInStock },
      { useCartStore },
      { default: SelectOptionFactory },
      { default: GA4Controller },
      { default: AnalyticsItemFactory }
    ] = await Promise.all([
      import('@/services/helpers/ProductHelper'),
      import('@/stores/cart'),
      import('@/modules/select_option/SelectOptionFactory'),
      import('@/services/analytics/GA4Controller'),
      import('@/modules/tag_manager/AnalyticsItemFactory')
    ])
    cache = {
      getCartPayload,
      isInStock,
      SelectOptionFactory,
      cart: useCartStore(),
      GA4Controller,
      AnalyticsItemFactory
    }
    return cache
  }
})()

export default {
  name: 'Cta',
  components: { SvgWrapper },
  setup() {
    const el = ref(null)

    return { el, ...useListId(el) }
  },
  props: {
    product: Object,
    noText: {
      type: Boolean,
      default: false
    },
    preSelectedQty: {
      type: Number,
      default: null
    },
    index: Number
  },
  data() {
    return {
      uuId: '',
      requestState: REQUEST_STATES.NONE,
      selectedQty: null,
      isInStock: false,
      options: [],
      smallestQty: 1,
      isInitialized: false,
      isEnabled: false
    }
  },
  computed: {
    isPendingState() {
      return this.requestState === REQUEST_STATES.PENDING
    },
    isSuccessState() {
      return this.requestState === REQUEST_STATES.FINISHED
    }
  },
  methods: {
    async trackQuantityChange(oldQty, newQty) {
      const { GA4Controller, AnalyticsItemFactory } = await _getModules()
      const analyticsItem = AnalyticsItemFactory.getAnalyticsItem({
        item: this.product,
        itemListId: this.itemListId,
        index: this.index,
        quantity: newQty
      })
      void GA4Controller.pushChangeQtyEvent({
        analyticsItem,
        itemListId: this.itemListId,
        newQty,
        oldQty
      })
    },
    async onAddToCart() {
      if (this.requestState !== REQUEST_STATES.NONE) return
      this.requestState = REQUEST_STATES.PENDING

      const { getCartPayload, cart } = await _getModules()

      try {
        await cart.addItem(
          getCartPayload(
            this.product,
            this.selectedQty,
            this.index,
            this.itemListId
          )
        )
        this.requestState = REQUEST_STATES.FINISHED

        setTimeout(() => {
          this.requestState = REQUEST_STATES.NONE
        }, SUCCESS_STATE_DURATION)
      } catch (e) {
        this.handleCartError(e)
      }
    },
    handleCartError({ message }) {
      this.requestState = REQUEST_STATES.NONE
      if (message.includes('not in stock')) {
        this.isEnabled = false
        setTimeout(() => {
          throw new Error(message)
        }, 0)
      }
    },
    async refreshProductData() {
      const { isInStock, SelectOptionFactory } = await _getModules()

      this.uuId = `${this.product.product_id}_${Math.random()}`
      this.isInStock = isInStock(this.product)
      this.smallestQty = SelectOptionFactory.getSmallestQtyNumber(this.product)
      // An explicit prop (PDP passes its ?qty= override) wins over the
      // product's own merchant-configured default; last resort is the
      // smallest sellable increment rather than a hardcoded 1, so tiles never
      // preselect (or inject) a single bottle for products that may only be
      // sold in larger units.
      this.selectedQty =
        this.preSelectedQty ||
        this.product.pre_selected_qty ||
        this.smallestQty ||
        1
      this.options = SelectOptionFactory.getQtyOptionsForProduct(
        this.product,
        this.noText,
        this.selectedQty
      )
      this.isEnabled = this.isInStock && this.smallestQty !== null
      this.isInitialized = Boolean(
        this.product.product_id && this.product.campaign_id
      )
    }
  },
  async created() {
    await this.refreshProductData()
  },
  watch: {
    async selectedQty(newValue, oldValue) {
      if (oldValue) {
        await this.trackQuantityChange(oldValue, newValue)
      }
    },
    async product() {
      await this.refreshProductData()
    }
  }
}
</script>

<style scoped>
.cta {
  height: 56px;
}
.cta :deep(.v-field__outline) {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-right: 0;
}
.cta :deep(.v-btn:not(.v-btn--disabled)) {
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
}
</style>
